import React, { useMemo } from 'react';
import classNames from 'classnames';

import i18n from '@nextroll/ar-i18n';
import { Option, Select } from '@nextroll/ar-style-base';
import { NodeDropdownDefaultProps } from 'types/rofl';

interface IProps<T> extends NodeDropdownDefaultProps<T> {
    isDisabled?: boolean;
    header?: JSX.Element;

    // callbacks for rendering dropdown options
    renderOption?: (option: T) => string;
    renderWarning?: (option: T) => boolean;
    renderError?: (option: T) => boolean;
    renderTooltip?: (option: T) => string;

    // callbacks for dropdown related events
    onSearch?: (search: string, option: T) => boolean;
    onRefresh?: () => void;

    // whether or not to allow multiple values to be selected
    multiple?: boolean;

    // how the options are selected
    label?: string | ((selectedOptions: T[]) => string);

    // extra classnames
    className?: string;

    // specific data-testId value for unit test
    testId?: string;
}

export const NodePanelDropDown = <T, >(props: IProps<T>) => {
    const {
        data, loading, isDisabled, warning, error, multiple, label, className, testId, errorElement, // data
        renderOption, renderTooltip, // callbacks
        allowRefresh, onRefresh, // assets for the reload button
    } = props;

    // sort the dropdown options before rendering
    const sortedData = useMemo(
        () => (data || []).sort((a, b) => (renderOption?.(a) || '').localeCompare(renderOption?.(b) || '')),
        [data, renderOption],
    );

    // reload button and loading spinner
    const refreshButtonOrSpinner = useMemo(() => {
        if (!allowRefresh || !onRefresh) {
            return null;
        }
        if (loading) {
            return <span data-testid='workflow-node-panel-loading-spinner' className='loading-spinner-small' />;
        }
        return <i className='ar-icon ar-icon-fw fa-sync-alt' onClick={() => onRefresh?.()} />;
    }, [allowRefresh, onRefresh, loading]);

    return (
        <>
            <div className='workflow-node-panel-select-dropdown' data-testid={testId}>
                <Select
                    i18n={i18n}
                    multiple={multiple}
                    label={label}
                    showSearch={!!props.onSearch && sortedData.length > 0}
                    searchCallback={props.onSearch}
                    placeholder={props.placeholder || i18n.gettext('Select List')}
                    customHeader={props.header}
                    onOptionSelect={props.onSelect}
                    disabled={loading || isDisabled}
                    className={classNames({
                        warning: warning && !error,
                        error,
                        'workflow-node-panel-select-dropdown-allow-refresh': props.allowRefresh,
                        [className || '']: !!className, // extra classnames, if provided
                    })}
                    data-test-id='workflow-node-panel-dropdown-error'
                >
                    {sortedData.map((option, i) => {
                        // todo - renderError not used anywhere (always empty)
                        // and the renderWarning is only used with the trigger node.
                        // should we sill have this logic as is?
                        const isWarning = props.renderWarning?.(option);
                        const isError = props.renderError?.(option);
                        return (
                            <Option
                                key={`workflow-node-panel-dropdown-option-${i.toString()}`}
                                value={option}
                                info={(option as T & { info: string })?.info}
                                disabled={isWarning || isError}
                                selected={props.renderSelected?.(option)}
                            >
                                <>
                                    {isWarning && (
                                        <i className='ar-icon ar-icon-fw fa-exclamation-triangle text-warning' />
                                    )}
                                    {isError && (
                                        <i className='ar-icon ar-icon-fw fa-exclamation-circle text-danger' />
                                    )}
                                    {renderOption?.(option) || option}
                                    {renderTooltip && <p className='option-tooltip'>{renderTooltip(option)}</p>}
                                </>
                            </Option>
                        );
                    })}
                </Select>

                {refreshButtonOrSpinner}
            </div>

            {/* warning message if needed */}
            {warning && <span className='workflow-node-panel-select-dropdown-warning-msg'>{warning}</span>}

            {/* error message if needed */}
            {errorElement
                ?? (error && <span className='workflow-node-panel-select-dropdown-error-msg'>{error}</span>)}
        </>
    );
};
